import { NgIf, NgFor } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import { SummaryCardComponent } from '@next/core-lib/components';
import { TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import {
  Calculation,
  CountryOptions,
  ExtendedSimulation,
  ExtendedSimulationExtendedBaseLine,
  FormValues,
  GenderOptions,
} from '../../../models/feed-profit.model';
import * as actions from '../../../store/feed-profit.actions';
import { BaselineFeedersComponent } from '../../shared/baseline-feeders/baseline-feeders.component';

@Component({
  selector: 'app-baseline',
  templateUrl: './baseline.component.html',
  styleUrls: ['./baseline.component.scss'],
  imports: [
    NgIf,
    SummaryCardComponent,
    ReactiveFormsModule,
    MatSelect,
    NgFor,
    MatOption,
    BaselineFeedersComponent,
    TranslatePipe,
  ],
})
export class BaselineComponent implements OnInit, OnDestroy {
  @Input() calculation: Calculation;
  @ViewChild(BaselineFeedersComponent) baselineFeedersComponent: BaselineFeedersComponent;

  @Output()
  baseLineChanged: EventEmitter<Calculation> = new EventEmitter<Calculation>();

  formSubscription: Subscription;

  form: FormGroup = new FormGroup({
    genders: new FormControl(GenderOptions.BOAR_GILT),
    country: new FormControl(''),
  });

  genderOptions: string[] = Object.values(GenderOptions);
  countryOptions: string[] = Object.values(CountryOptions);

  constructor(private readonly store: Store<RootState>) {}
  ngOnInit(): void {
    this.updateProducts({
      ...this.calculation,
    });

    this.setBaselineValues(this.calculation);

    this.formSubscription = this.form.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((values: FormValues) => {
        const genders =
          values.genders === 'barrow_gilt' ? GenderOptions.BARROW_GILT : GenderOptions.BOAR_GILT;

        const country = values.country
          ? values.country === 'nl'
            ? CountryOptions.NL
            : values.country === 'du'
              ? CountryOptions.DU
              : CountryOptions.BIO
          : null;

        this.updateProducts({
          ...this.calculation,
          country,
          genders,
        });
        this.changeGenderSelection({ value: genders });
      });
  }

  setBaselineValues(calculation: Calculation) {
    if (calculation) {
      const genders =
        calculation.genders === 'barrow_gilt' ? GenderOptions.BARROW_GILT : GenderOptions.BOAR_GILT;
      this.form.controls.genders.setValue(genders);
      this.form.controls.country.setValue(calculation.country ?? '');
    }
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  updateProducts(calculation: Calculation, emitEvent = true) {
    this.baselineFeedersComponent?.updateCalculation(calculation, emitEvent);
    this.baseLineChanged.emit(calculation);
  }

  updateFeeders(calculation: Calculation) {
    this.baseLineChanged.emit(calculation);
  }

  changeGenderSelection($event: MatSelectChange | { value: string }) {
    const genders =
      $event.value === 'barrow_gilt' ? GenderOptions.BARROW_GILT : GenderOptions.BOAR_GILT;

    this.baselineFeedersComponent?.changeGenderSelection(<string>$event?.value);
    this.form.controls.genders.setValue(genders || GenderOptions.BOAR_GILT, {
      emitEvent: false,
    });
    this.store.dispatch(actions.setIsChanged({ isChanged: true }));
  }

  changeCountrySelection($event: MatSelectChange | { value: string }) {
    const country =
      $event.value === 'nl'
        ? CountryOptions.NL
        : $event.value === 'du'
          ? CountryOptions.DU
          : $event.value === 'bio'
            ? CountryOptions.BIO
            : null;

    this.baselineFeedersComponent?.updateCalculation({
      ...this.baselineFeedersComponent?.calculation,
      country,
    });
    this.form.controls.country.setValue(country ?? '', {
      emitEvent: false,
    });
    this.store.dispatch(actions.setIsChanged({ isChanged: true }));
  }

  getQuickComparisonSimulation(calculation: Calculation) {
    return {
      startWeight: calculation?.extendedBaseLine?.startWeight ?? undefined,
      pigletPrice: calculation?.extendedBaseLine?.growFinisherPrice ?? undefined,
      slaughteredWeightInKg: calculation?.extendedBaseLine?.slaughteredWeightInKg ?? undefined,
      revenuePerKg: calculation?.extendedBaseLine?.revenuePerKg ?? undefined,
      feedPackagePrice: 0,
    };
  }

  getComparisonSimulation(calculation: Calculation) {
    return <ExtendedSimulation>{
      startWeight: calculation?.extendedBaseLine?.startWeight ?? undefined,
      pigletPrice: calculation?.extendedBaseLine?.growFinisherPrice ?? undefined,
      slaughteredWeightInKg: calculation?.extendedBaseLine?.slaughteredWeightInKg ?? undefined,
      revenuePerKg: calculation?.extendedBaseLine?.revenuePerKg ?? undefined,
      feedPackagePrice: 0,

      feeders: calculation?.feeders,
      calculatedFeeder: calculation?.calculatedFeeder,
      baseLine: calculation?.baseLine,
      extendedBaseLine: <ExtendedSimulationExtendedBaseLine>{
        ...calculation?.extendedBaseLine,
        growFinisherPrice: calculation?.extendedBaseLine?.growFinisherPrice,
        feedPackagePrice: 0,
      },
    };
  }
}
